/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useRef, useState } from 'react';
import SpellLevelSelector from './SpellLevelSelector';
import Spell from '../model/Spell';
import AttributeValues from "../model/AttributeValues";
import ClassService from '../services/ClassService';
import SpellbookSelector from './SpellbookSelector';
import SpellService from '../services/SpellService';
import { ClassDetails } from '../model/ClassDetails';
import Class from '../model/Class';

interface SpellLevelsSelectorProps {
  primaryClassDetails: ClassDetails | null;
  secondaryClassDetails: ClassDetails | null;
  spellsBySpellLevelByClass: Map<string, Map<number, Spell[]>>;
  attributeValues: AttributeValues;
  primaryAttribute?: string;
  secondaryAttribute?: string;
  onPrimaryAttributeChanged?: (attribute: string) => void;
  onSecondaryAttributeChanged?: (attribute: string) => void;
  onSpellbookSpellsPrimaryChanged: (level: number, spells: Spell[]) => void;
  onSpellsPreparedPrimaryChanged: (level: number, spells: Spell[]) => void;
  onSpellbookSpellsSecondaryChanged: (level: number, spells: Spell[]) => void;
  onSpellsPreparedSecondaryChanged: (level: number, spells: Spell[]) => void;
  openLevel: number; // -1 means nothing open
  setOpenLevel: (level: number) => void;
  onClickOutside: (event: MouseEvent) => void;
  setClassDetails: ((className: string, classLevel: number, isPrimary: boolean) => void) | undefined;
}

const SpellLevelsSelector: React.FC<SpellLevelsSelectorProps> = ({
  primaryClassDetails, secondaryClassDetails,
  spellsBySpellLevelByClass, attributeValues,
  primaryAttribute, secondaryAttribute,
  onPrimaryAttributeChanged, onSecondaryAttributeChanged,
  onSpellbookSpellsPrimaryChanged, onSpellsPreparedPrimaryChanged,
  onSpellbookSpellsSecondaryChanged, onSpellsPreparedSecondaryChanged,
  openLevel, setOpenLevel, onClickOutside, setClassDetails = undefined
}) => {
  const ATTRIBUTE_OPTIONS: { key: string; label: string }[] = [
    { key: 'intelligence', label: '(Int)' },
    { key: 'wisdom',       label: '(Wis)' },
    { key: 'charisma',     label: '(Cha)' },
  ];
  const [castingClassesNames,  setCastingClassesByName] = useState<string[]>([]);
  const [classesByName,        setClassesByName]        = useState<Map<string, Class>>(new Map());
  const [primaryClassName,     setPrimaryClassName]     = useState<string | undefined>(undefined);
  const [secondaryClassName,   setSecondaryClassName]   = useState<string | undefined>(undefined);
  const [primaryClassLevel,    setPrimaryClassLevel]    = useState<number>(1);
  const [secondaryClassLevel,  setSecondaryClassLevel]  = useState<number>(0);

  useEffect(() => {
    let name = primaryClassDetails?.classInfo?.name;
    if (!name || !castingClassesNames.includes(name)) {
      name = primaryClassDetails?.archetype?.name;
    }
    setPrimaryClassName(name);

    name = secondaryClassDetails?.classInfo?.name;
    if (!name || !castingClassesNames.includes(name)) {
      name = secondaryClassDetails?.archetype?.name;
    }
    setSecondaryClassName(name);

    setPrimaryClassLevel(primaryClassDetails?.classLevel || 0);
    setSecondaryClassLevel(secondaryClassDetails?.classLevel || 0);
  }, [primaryClassDetails, secondaryClassDetails, castingClassesNames]);

  useEffect(() => {
    // Define the function inside useEffect
    const fetchSpellLevelData = async () => {
      try {
        const classData = await ClassService.fetchClasses(false);

        const casterClassesNames: string[] = [];
        for (const classInfo of classData) {
          classesByName.set(classInfo.name, classInfo);
          if ((classInfo.base == null) && ClassService.getSpellsKnownForClassLevel(classInfo, 20, attributeValues) > 0) {
            casterClassesNames.push(classInfo.name);
          }
        }
        for (const classInfo of classData) {
          if (classInfo.base != null) {
            if (ClassService.getSpellsKnownForClassLevel(classInfo, 20, attributeValues) > 0) {
              if (!casterClassesNames.includes(classInfo.base.name)) {
                casterClassesNames.push(classInfo.name);
              }
            }
          }
        }
        casterClassesNames.push(ClassService.metaCaster.name);
        classesByName.set(ClassService.metaCaster.name, ClassService.metaCaster);
        setCastingClassesByName(casterClassesNames);
        setClassesByName(classesByName);

      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    // Call the function
    fetchSpellLevelData();
  }, []);

  const spellsArray = (classDetails: ClassDetails | null,
                       onSpellsPreparedChanged: (level: number, spells: Spell[]) => void,
                       spellLevelOffset: number
                       ): (JSX.Element | null)[] | null => {
    if (!classDetails?.classInfo) {
      return null;
    }

    const isWizard = classDetails.classInfo?.name.toLowerCase() === 'wizard';
    const spellsKnowableArrayBySpellLevelMap = spellsBySpellLevelByClass.get(classDetails.classInfo.name.toLowerCase() || '') ||
                                               spellsBySpellLevelByClass.get(classDetails.archetype?.name.toLowerCase() || '');

    if (!spellsKnowableArrayBySpellLevelMap) {
      return null;
    }

    const spellsKnowableArrayBySpellLevelArray: Spell[][] = [];
    for (let spellLvl = 0; spellLvl <= 9; spellLvl++) {
      const spells = [...(spellsKnowableArrayBySpellLevelMap.get(spellLvl) || [])];
      spellsKnowableArrayBySpellLevelArray[spellLvl] = spells.sort((a, b) => (a.name < b.name ? -1 : (a.name > b.name ? 1 : 0)));
    }

    let totalSpellsCount = ClassService.getSpellsKnownForClassLevel(classDetails.classInfo, classDetails.classLevel, attributeValues);
    let maxSpellCountByLevel = ClassService.getSpellsKnowableBySpellLevelForClassLevel(classDetails.classInfo, classDetails.classLevel, attributeValues);
    
    if (!totalSpellsCount && classDetails.archetype) {
      totalSpellsCount = ClassService.getSpellsKnownForClassLevel(classDetails.archetype, classDetails.classLevel, attributeValues);
      maxSpellCountByLevel = ClassService.getSpellsKnowableBySpellLevelForClassLevel(classDetails.archetype, classDetails.classLevel, attributeValues);
    }

    let spellsRequiredToMatchFilter = 0;
    let spellFilterFunction: ((spell: Spell) => boolean) | null = null;

    if (classDetails.archetype?.name.toLowerCase() === 'arcane warrior') {
      if (classDetails.classLevel > 8) {
        spellsRequiredToMatchFilter = 4;
      } else if (classDetails.classLevel === 8) {
        spellsRequiredToMatchFilter = 3;
      } else {
        spellsRequiredToMatchFilter = totalSpellsCount - 1;
      }
      spellFilterFunction = (spell: Spell) => {
        return spell.school.key === 'evocation' ||
               spell.school.key === 'abjuration';
      };
    }

    // Eldritch Tricksters are required to select the 'Mage Hand' cantrip.
    if (classDetails.archetype?.name.toLowerCase() === 'eldritch trickster') {
      for (const cantrip of spellsKnowableArrayBySpellLevelArray[0]) {
        if (cantrip.name.toLowerCase() === 'mage hand') {
          if (!classDetails.spellsPreparedArrayBySpellLevel[0].includes(cantrip)) {
            classDetails.spellsPreparedArrayBySpellLevel[0].push(cantrip);
          }
          break;
        }
      }
    }
    // Underfoots are required to select the 'shillelagh' cantrip
    if (classDetails.archetype?.name.toLowerCase() === 'underfoot') {
      for (const cantrip of spellsKnowableArrayBySpellLevelArray[0]) {
        if (cantrip.name.toLowerCase() === 'shillelagh') {
          if (!classDetails.spellsPreparedArrayBySpellLevel[0].includes(cantrip)) {
            classDetails.spellsPreparedArrayBySpellLevel[0].push(cantrip);
          }
          break;
        }
      }
    }

    let totalSpellbookCount = 0;
    let totalPreparedCount = 0;
    let spellsPreparedThatMatchFilter = 0;
    
    for (let lvl = 1; lvl <= 9; lvl++) {
      totalSpellbookCount += classDetails.spellbookSpellsArrayBySpellLevel[lvl]?.filter(o => o).length || 0;
      totalPreparedCount += classDetails.spellsPreparedArrayBySpellLevel[lvl]?.filter(o => o).length || 0;
      if (spellsRequiredToMatchFilter > 0 && spellFilterFunction) {
        spellsPreparedThatMatchFilter += classDetails.spellsPreparedArrayBySpellLevel[lvl].filter(v => spellFilterFunction && spellFilterFunction(v)).length;
      }
    }

    if (isWizard) {
      totalSpellsCount = 0;
      for (let lvl = 1; lvl <= 9; lvl++) {
        totalSpellsCount += maxSpellCountByLevel[lvl] || 0;
      }
    }
    
    const totalSpellsLeft = totalSpellsCount - totalPreparedCount;

    const maxSpellsByLevel: number[] = [];
    maxSpellsByLevel[0] = maxSpellCountByLevel[0] || 0;

    let maxSpells = 0;
    for (let spellLevel = 9; spellLevel > 0; spellLevel--) {
      maxSpells += (maxSpellCountByLevel[spellLevel] || 0);
      maxSpellsByLevel[spellLevel] = maxSpells;
    }

    if (totalSpellsLeft > (spellsRequiredToMatchFilter - spellsPreparedThatMatchFilter)) {
      spellFilterFunction = null;
    }

    const spellLevelSelectors: (JSX.Element | null)[] = [];
    let sourceSpellsArrayBySpellLevelArray = [...spellsKnowableArrayBySpellLevelArray];
    if (isWizard) {
      sourceSpellsArrayBySpellLevelArray = classDetails.spellbookSpellsArrayBySpellLevel;
      sourceSpellsArrayBySpellLevelArray[0] = spellsKnowableArrayBySpellLevelArray[0];
    }

    for (let spellLevel = 0; spellLevel <= 9; spellLevel++) {
      const preparedCount = classDetails.spellsPreparedArrayBySpellLevel[spellLevel]?.filter(o => o).length || 0;

      let maxAllowed = maxSpellsByLevel[spellLevel] || 0;
      if (spellLevel > 0) {
        maxAllowed = Math.min((totalSpellsLeft + preparedCount), maxAllowed);
      }

      if (preparedCount > 0 || maxAllowed > 0) {
        const notes = `(${preparedCount} / ${maxAllowed })`;
        spellLevelSelectors.push(
          <SpellLevelSelector notes={notes} maxSpellCount={maxAllowed} columns={(isWizard && (spellLevel > 0)) ? 1 : 3}
                              spellsKnowableArray={sourceSpellsArrayBySpellLevelArray[spellLevel] || []}
                              spellsSelectedArray={classDetails.spellsPreparedArrayBySpellLevel[spellLevel] || []}
                              onSpellsSelectedChanged={onSpellsPreparedChanged} showGrouping={!isWizard || spellLevel === 0}
                              filterFunction={spellLevel > 0 && spellFilterFunction || undefined}
                              level={spellLevel}
                              openLevel={openLevel - spellLevelOffset} 
                              setOpenLevel={(level) => (level > -1) ? setOpenLevel(level + spellLevelOffset) : setOpenLevel(-1)}
                              onClickOutside={onClickOutside} />
        );
      } else {
        spellLevelSelectors.push(null);
      }
    }
    return spellLevelSelectors;
  }

  const primarySpells   = spellsArray(primaryClassDetails,   onSpellsPreparedPrimaryChanged,   30);
  const secondarySpells = spellsArray(secondaryClassDetails, onSpellsPreparedSecondaryChanged, 60);

  if (primarySpells?.length === 0 && secondarySpells?.length === 0 && setClassDetails === undefined) {
    return null;
  }
  const slotsByLevel = SpellService.getSpellSlots(primaryClassDetails, secondaryClassDetails);
  const warlockSpellSlots = SpellService.getWarlockSpellSlots(primaryClassDetails?.classInfo || null, primaryClassDetails?.classLevel || 0) ||
                            SpellService.getWarlockSpellSlots(secondaryClassDetails?.classInfo || null, secondaryClassDetails?.classLevel || 0) || [];
  if ((slotsByLevel.length < 2 || slotsByLevel[1] == 0) && setClassDetails === undefined && warlockSpellSlots === null) {
    // None of the these classes are spellscasters
    return null;
  }

  const slots = [];
  let maxLevel = 0;
  for (let spellLevel = 0; spellLevel <= 9; spellLevel++) {
    let slot = spellLevel == 0 ? '--' : slotsByLevel[spellLevel];
    if ((warlockSpellSlots[spellLevel] || 0) > 0) {
      slot = (slot ? (slot + "/") : '') + warlockSpellSlots[spellLevel] + "*";
    }
    slots[spellLevel] = slot || '';
    if (slot) {
      maxLevel = spellLevel;
    }
  }

  if (maxLevel === 0 && !setClassDetails) {
    return <div>No casting classes selected.</div>;
  }

  const tableRows: JSX.Element[] = [];
  for (let spellLevel = 0; spellLevel <= maxLevel; spellLevel++) {
    tableRows.push(
      <tr key={tableRows.length}>
        <td>{spellLevel == 0 ? 'Cantrips' : ClassService.getLevelString(spellLevel)}</td>
        <td>{slots[spellLevel]}</td>
        { (setClassDetails || (primarySpells && (spellLevel < primarySpells.length))) && <td>{primarySpells && (spellLevel < primarySpells.length) && primarySpells[spellLevel]}</td>}
        { ((setClassDetails && primaryClassName && !primaryClassName?.includes('Caster')) || (secondarySpells && (spellLevel < secondarySpells.length))) && <td>{(secondarySpells && (spellLevel < secondarySpells.length)) &&secondarySpells[spellLevel]}</td>}
      </tr>
      );
  }

  const handleClassChange = (newClassName: string, isPrimary: boolean) => {
    setClassDetails && setClassDetails(newClassName, (isPrimary ? primaryClassLevel : secondaryClassLevel) || 0, isPrimary);
  }
  const handleClassLevelChange = (newClassLevel: string, isPrimary: boolean) => {
    setClassDetails && setClassDetails((isPrimary ? primaryClassName : secondaryClassName) || '', parseInt(newClassLevel), isPrimary);
  }
  
  return (<>
      {(primaryClassDetails?.classInfo?.name === 'Wizard') && (
        <SpellbookSelector classDetails={primaryClassDetails}
                           spellsBySpellLevelByClass={spellsBySpellLevelByClass}
                           onSpellbookSpellsChanged={onSpellbookSpellsPrimaryChanged}
                           openLevel={openLevel} setOpenLevel={setOpenLevel} onClickOutside={onClickOutside}/>
        )
      }
      {(secondaryClassDetails?.classInfo?.name === 'Wizard') && (
        <SpellbookSelector classDetails={secondaryClassDetails}
                           spellsBySpellLevelByClass={spellsBySpellLevelByClass}
                           onSpellbookSpellsChanged={onSpellbookSpellsSecondaryChanged}
                           openLevel={openLevel} setOpenLevel={setOpenLevel} onClickOutside={onClickOutside}/>
        )
      }
      <h3>Prepared Spells:</h3>
      <table className="markdown-table">
        <thead>
          <tr>
              <th>Level</th>
              <th>Slots</th>
              {
                setClassDetails && (<>
                <th>
                    Primary Class
                    {primaryClassName && onPrimaryAttributeChanged && (<>
                      &nbsp;
                      <select className="attribute-selector" value={primaryAttribute || ''}
                              onChange={e => onPrimaryAttributeChanged(e.target.value)}
                              style={{width: '45px', fontSize: 'x-small', background: 'none', border: '0px'}}
                              title="Spellcasting ability">
                        {ATTRIBUTE_OPTIONS.map(a => (
                          <option key={a.key} value={a.key}>{a.label}</option>
                        ))}
                      </select>
                    </>)}
                    <br/>
                    <select className="class-selector" value={primaryClassName || ''}
                            onChange={e => handleClassChange(e.target.value, true)}
                            style={{width: '80px', fontSize: 'smaller'}}>
                      <option key='null' value='null'>Select Class</option>
                      {castingClassesNames.map(castingClass => (
                          <option key={castingClass} value={castingClass}>
                              {castingClass}
                          </option>
                        ))}
                    </select> -
                    <select className="level-selector" value={primaryClassLevel || 1}
                            onChange={e => handleClassLevelChange(e.target.value, true)}
                            style={{width: '30px', fontSize: 'x-small'}}>
                      {Array.from({ length: 20 }, (_, i) => <option key={i} value={i + 1}>{i + 1}</option>)}
                      <option key={21} value={21}>Max</option>
                    </select>
                </th>
                {(primaryClassName && !primaryClassName?.includes('Caster')) && (<th>
                  Secondary Class
                    {secondaryClassName && onSecondaryAttributeChanged && (<>
                      &nbsp;
                      <select className="attribute-selector" value={secondaryAttribute || ''}
                              onChange={e => onSecondaryAttributeChanged(e.target.value)}
                              style={{width: '45px', fontSize: 'x-small', background: 'none', border: '0px'}}
                              title="Spellcasting ability">
                        {ATTRIBUTE_OPTIONS.map(a => (
                          <option key={a.key} value={a.key}>{a.label}</option>
                        ))}
                      </select>
                    </>)}
                  <br/>
                  <select className="class-selector" value={secondaryClassName || ''}
                            onChange={e => handleClassChange(e.target.value, false)}
                            style={{width: '80px', fontSize: 'smaller'}}>
                      <option key='null' value='null'>Select Class</option>
                      {castingClassesNames.map(castingClass => (
                          <option key={castingClass} value={castingClass}>
                              {castingClass}
                          </option>
                        ))}
                    </select> -
                    <select className="level-selector" value={secondaryClassLevel || 1}
                            onChange={e => handleClassLevelChange(e.target.value, false)}
                            style={{width: '30px', fontSize: 'x-small'}}>
                      {Array.from({ length: 20 }, (_, i) => <option key={i} value={i + 1}>{i + 1}</option>)}
                      <option key={21} value={21}>Max</option>
                    </select>
                </th>)}
                </>)
              }
              {setClassDetails == undefined && primarySpells?.length && (<th>{`${primaryClassDetails?.classInfo?.name} - ${primaryClassDetails?.classLevel || 0}`}</th>)}
              {setClassDetails == undefined && secondarySpells?.length && (<th>{`${secondaryClassDetails?.classInfo?.name} - ${secondaryClassDetails?.classLevel || 0}`}</th>)}
            </tr>
        </thead>
        <tbody>
          {tableRows}
        </tbody>
      </table>
    </>
  );
};

export default SpellLevelsSelector;
